import React, { useState } from 'react';
import { useCollegePrepContext } from '../../contexts/CollegePrepContext';
import DiscoveryQuestions from './InterestDiscovery/DiscoveryQuestions';
import ProgressTracker from './InterestDiscovery/ProgressTracker';
import AIAnalysis from './InterestDiscovery/AIAnalysis';

const InterestProfile = () => {
  const { interests, updateInterests } = useCollegePrepContext();
  const [discoveryResponses, setDiscoveryResponses] = useState(interests.discoveryResponses || {});
  const [analysisResults, setAnalysisResults] = useState(interests.analysisResults || null);
  const [activeView, setActiveView] = useState('discovery'); // 'discovery', 'progress', 'analysis'

  const handleDiscoveryResponseChange = (responses) => {
    setDiscoveryResponses(responses);
    updateInterests({ 
      ...interests, 
      discoveryResponses: responses,
      lastUpdated: new Date().toISOString()
    });
  };

  const handleAnalysisComplete = (analysis) => {
    setAnalysisResults(analysis);
    updateInterests({ 
      ...interests, 
      discoveryResponses,
      analysisResults: analysis,
      lastUpdated: new Date().toISOString()
    });
    setActiveView('analysis');
  };

  const getCompletionCount = () => {
    return Object.values(discoveryResponses).filter(response => response && response.trim().length > 0).length;
  };

  const getProgressPercentage = () => {
    const totalQuestions = 16;
    return Math.round((getCompletionCount() / totalQuestions) * 100);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Discover Your Interests</h2>
            <p className="text-stone-600">
              Deep discovery questions to understand what truly excites you and find your perfect summer programs.
            </p>
          </div>
          
          {/* View Toggle */}
          <div className="flex bg-stone-100 rounded-lg p-1">
            <button
              onClick={() => setActiveView('discovery')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                activeView === 'discovery'
                  ? 'bg-white text-emerald-600 shadow-sm'
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              Questions
            </button>
            <button
              onClick={() => setActiveView('progress')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                activeView === 'progress'
                  ? 'bg-white text-emerald-600 shadow-sm'
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              Progress
            </button>
            {analysisResults && (
              <button
                onClick={() => setActiveView('analysis')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  activeView === 'analysis'
                    ? 'bg-white text-emerald-600 shadow-sm'
                    : 'text-stone-600 hover:text-stone-900'
                }`}
              >
                Results
              </button>
            )}
          </div>
        </div>
        
        {/* Quick Stats */}
        {getCompletionCount() > 0 && (
          <div className="flex items-center gap-4 text-sm">
            <div className="text-emerald-600 font-medium">
              {getCompletionCount()} questions completed
            </div>
            <div className="text-stone-400">•</div>
            <div className="text-emerald-600">
              {getProgressPercentage()}% progress
            </div>
            {analysisResults && (
              <>
                <div className="text-stone-400">•</div>
                <div className="text-azure-600 font-medium">
                  🎯 Analysis complete
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Main Content Based on Active View */}
      {activeView === 'discovery' && (
        <DiscoveryQuestions 
          onResponseChange={handleDiscoveryResponseChange}
          initialResponses={discoveryResponses}
        />
      )}

      {activeView === 'progress' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ProgressTracker 
            responses={discoveryResponses}
            totalQuestions={16}
          />
          <AIAnalysis 
            responses={discoveryResponses}
            onAnalysisComplete={handleAnalysisComplete}
          />
        </div>
      )}

      {activeView === 'analysis' && analysisResults && (
        <AIAnalysis 
          responses={discoveryResponses}
          onAnalysisComplete={handleAnalysisComplete}
        />
      )}
    </div>
  );
};

export default InterestProfile;